import { FormattedMessage } from 'react-intl';
import { useState } from 'react';
import { IconLibrary } from '@product-framework/index';
import {
  SnackbarApiErrorMessage,
  UnSavedChangesDialog,
  useDataProvider,
  useDialog,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import { Box, FormHelperText, Typography } from '@mui/material';
import PermissionButton from '../PermissionButton';

function DialogContent({ firstName, lastName }) {
  return (
    <Box>
      <Typography>
        <FormattedMessage
          id="DO_YOU_WANT_TO_SEND_PASSWORD_RESET_EMAIL_TO"
          defaultMessage="Do you want to send password reset email to"
        />{' '}
        <Typography color="primary.main" component="span">
          {firstName} {lastName}
        </Typography>
        ?
      </Typography>
      <FormHelperText>
        <FormattedMessage
          id="PASWORD_RESET_EMAIL_WILL_BE_SENT"
          defaultMessage='If you click "Yes", a password reset email will been sent to the user. If the user does not receive the email within a few minutes, please ask them to check their spam or junk folder.'
        />
      </FormHelperText>
    </Box>
  );
}

function UserSendPasswordResetEmailDialogWithButton({
  email,
  firstName,
  lastName,
  permissionName,
  formRef = null,
}) {
  if (!permissionName) {
    throw new Error(
      'Please provide permission name for UserUpdatePasswordDialogWithButton',
    );
  }
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post, getApiUrl } = useDataProvider(provider);
  const snackbar = useSnackbar();
  const { confirmationRef } = useDialog();
  const { formatMessage } = useLocalization();
  const [isDirty, setIsDirty] = useState(false);

  const sendPasswordResetEmail = async () => {
    const url = `${getApiUrl()}/sxcl/v1/auth/send-reset-password-email`;
    const response = await post(url, {
      email,
    });
    if (response?.ok) {
      return response;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const sendPasswordResetEmailClick = () => {
    confirmationRef.current.init({
      Icon: <IconLibrary.PasswordEmail />,
      message: <DialogContent firstName={firstName} lastName={lastName} />,
      positiveButtonIcon: <IconLibrary.Yes />,
      negativeButtonIcon: <IconLibrary.Cancel />,
      positiveButtonText: 'Yes',
      negativeButtonText: 'Cancel',
      width: 360,
      title: 'Send Password Reset Email',
      onChange: async isYes => {
        if (isYes) {
          const response = await sendPasswordResetEmail();
          if (response?.ok) {
            snackbar.showMessage(
              formatMessage({
                id: 'USER_PASSWORD_RESET_EMAIL_WAS_SENT_SUCCESSFULLY',
                defaultMessage:
                  'User password reset email was sent successfully',
              }),
            );
          }
        }
        confirmationRef.current.setOpen(false);
      },
    });
    confirmationRef.current.setOpen(true);
  };

  return (
    <>
      <PermissionButton
        permissionName={permissionName}
        variant="contained"
        color="primary"
        startIcon={<IconLibrary.PasswordEmail />}
        onClick={() => {
          if (formRef?.current && formRef?.current?.isDirty()) {
            setIsDirty(true);
          } else {
            sendPasswordResetEmailClick();
          }
        }}>
        {formatMessage({
          id: 'SEND_PASSWORD_RESET_EMAIL',
          defaultMessage: 'Send Password Reset Email',
        })}
      </PermissionButton>
      <UnSavedChangesDialog
        open={isDirty}
        onStay={() => setIsDirty(false)}
        onLeave={() => {
          setIsDirty(false);
          formRef?.current?.reset();
          sendPasswordResetEmailClick();
        }}
      />
    </>
  );
}

export default UserSendPasswordResetEmailDialogWithButton;
